import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import { validators } from '../../../../_common/platform/ts/validators';
import * as appTheme from '../../../../_common/platform/ts/appTheme';

import { FontField } from '../../../../_common/platform/ts/FontField';
import { ColorBoxField } from '../../../../_common/platform/ts/ColorBoxField';
import NumberField from '../../../../_common/platform/ts/NumberField';
import BooleanField from '../../../../_common/platform/ts/BooleanField';
import StringField from '../../../../_common/platform/ts/StringField';
import SelectField from '../../../../_common/platform/ts/SelectField';
import { ReplaceNullValuesField } from '../../../../_common/platform/ts/ReplaceNullValuesField';
import { RadioGroupField } from '../../../../_common/platform/ts/RadioGroupField';

import { nullHandlingBehaviour, REPLACE_NULL_STRING_DEFAULT } from '../../../../_common/platform/ts/nullHandling';
import { themeVars } from './themeConstants';
import { WidgetPreview } from './previews/WidgetPreview';

const template = require('!!raw-loader!pdc-platform/src/widgetTheme.template.css').default;

template;

declare var Color: any;

var _init = [];

export function init(glob: IGlob) {
    if (_.indexOf(_init, glob) !== -1) {
        return;
    }

    appTheme.registerSections([
        {
            id: 'widget',
            name: 'Widget General',
            preview: WidgetPreview,
            previewClassName: 'preview-widget',

            themeType: 'Widget',
            visibility: ['application', 'workspace', 'widget']
        },
        {
            id: 'widget-capabilities',
            name: 'Widget Capabilities',
            previewClassName: 'preview-widget',
            parent: 'widget',
            themeType: 'Widget Capabilities',
            visibility: ['application', 'widget', 'workspace']
        },
        //LL: 18.07.21:
        {
            id: 'general-widget-grid',
            name: 'General Settings for Grids',
            alwaysFirst: true, //LL: 19.07.21
            parent: 'widget',
            themeType: 'GeneralGrid',
            visibility: [
                'application',
                'workspace',
                'pdc-widget-grid',
                'pdc-helpdesk-issue-grid',
                'pdc-widget-data-edit',
                'pdc-widget-pivot',
                'pdc-widget-treelist' //LL: 15.02.24: TL
            ]
        },

        {
            id: 'caching',
            name: 'Caching and Timeout',
            themeType: 'Caching',
            visibility: ['application', 'workspace', 'widget', 'inline']
        },

        {
            id: 'nullHandling',
            name: 'NULL Handling',

            themeType: 'NullHandling',
            visibility: ['application', 'workspace', 'widget', 'inline']
        }
    ]);

    appTheme.registerGroups([
        {
            id: 'widget-font',
            name: 'Widget Font',
            section: themeVars.section_widget
        },

        {
            id: 'widget-header-font-group',
            name: 'Widget Header Font',
            section: themeVars.section_widget
        },

        {
            id: themeVars.group_clickToFilter,
            name: 'Click To Filter',
            section: themeVars.section_widget
        },

        {
            id: 'general-widget-grid-header-color-group',
            name: 'Grid Column Header Color (for future use)',
            section: 'general-widget-grid'
        },

        {
            id: 'general-widget-grid-header-font-group',
            name: 'Grid Column Header Font',
            section: 'general-widget-grid'
        },

        {
            id: 'widget-loading-panel-group',
            name: 'Loading Panel',
            section: 'widget-capabilities'
        },
        {
            id: 'widget-print-group',
            name: 'Print',
            section: 'widget-capabilities'
        },
        {
            id: 'widget-generate-pdf-group',
            name: 'Generate PDF',
            section: 'widget-capabilities'
        },
        {
            id: 'widget-export-group',
            name: 'Excel',
            section: 'widget-capabilities'
        }
    ]);

    appTheme.registerVars([
        {
            id: 'widget-show-print',
            param: new BooleanField(glob, 'Show Print Button in Widget Toolbar', [], false),
            section: 'widget-capabilities',
            group: 'widget-print-group'
        },
        {
            id: themeVars.widgetShowPrintButton,
            param: new BooleanField(glob, 'Show Print Button in Widget Header', [], false),
            section: 'widget-capabilities',
            group: 'widget-print-group'
        },
        {
            id: 'widget-show-print-button-in-menu',
            param: new BooleanField(glob, 'Show Print Iitem in Widget Menu', [], true),
            section: 'widget-capabilities',
            group: 'widget-print-group'
        },
        {
            id: 'widget-show-pdf',
            param: new BooleanField(glob, 'Show Generate PDF Button in Widget Toolbar', [], false),
            section: 'widget-capabilities',
            group: 'widget-generate-pdf-group'
        },
        {
            id: themeVars.widgetShowPdfButton,
            param: new BooleanField(glob, 'Show Generate PDF Button in Widget Header', [], false),
            section: 'widget-capabilities',
            group: 'widget-generate-pdf-group'
        },
        {
            id: 'widget-show-pdf-in-menu',
            param: new BooleanField(glob, 'Show Generate PDF Item in Widget Menu', [], true),
            section: 'widget-capabilities',
            group: 'widget-generate-pdf-group'
        },
        {
            id: 'widget-show-export-to-excel',
            param: new BooleanField(glob, 'Show Export to Excel in Widget Toolbar', [], false),
            section: 'widget-capabilities',
            group: 'widget-export-group'
        },
        {
            id: themeVars.widgetShowExportToExcelButton,
            param: new BooleanField(glob, 'Show Export to Excel in Widget Header', [], false),
            section: 'widget-capabilities',
            group: 'widget-export-group'
        },
        {
            id: 'widget-show-export-to-excel-in-menu',
            param: new BooleanField(glob, 'Show Export to Excel Item in Widget Menu', [], true),
            section: 'widget-capabilities',
            group: 'widget-export-group'
        },

        {
            id: 'widget-font',
            param: new FontField(
                glob,
                'Font Face',
                [],
                function (theme) {
                    return theme['#font-main'];
                },
                {}
            ),
            section: 'widget',
            group: 'widget-font',
            deps: ['#font-main']
        },
        {
            id: 'widget-font-size',
            param: new SelectField(
                glob,
                'Font Size',
                [],
                function (theme) {
                    return theme['font-main-size'];
                },
                {
                    values: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ],
                    display: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ]
                }
            ),
            section: 'widget',
            group: 'widget-font',
            deps: ['font-main-size']
        },
        {
            id: 'widget-font-weight',
            param: new SelectField(
                glob,
                'Font Weight',
                [],
                function (theme) {
                    return theme['font-main-weight'];
                },
                {
                    values: ['lighter', 'normal', 'bold', 'bolder'],
                    display: ['lighter', 'normal', 'bold', 'bolder']
                }
            ),
            section: 'widget',
            group: 'widget-font',
            deps: ['font-main-weight']
        },
        {
            id: 'widget-font-italic',
            param: new BooleanField(glob, 'Italic', [], function (theme) {
                return theme['font-main-italic'];
            }),
            section: 'widget',
            group: 'widget-font',
            deps: ['font-main-italic']
        },
        {
            id: 'widget-border-width',
            param: new NumberField(
                glob,
                'Widget Frame Border Width (0-20)',
                [validators.number.range(0, 20), validators.number.integer],
                1,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'widget'
        },
        {
            id: 'widget-border-color',
            param: new ColorBoxField(
                glob,
                'Widget Frame Border Color',
                [],
                function (theme) {
                    return Color(theme['primary-bg']).lighten(20).hexString(); //mix(Color(theme['body-bg']), 0.5).hexString();
                },
                { hideTransparency: false }
            ),

            section: 'widget',
            deps: ['primary-bg']
        },
        {
            id: 'widget-border-radius',
            param: new NumberField(
                glob,
                'Widget Frame Border Radius (0-20)',
                [validators.number.range(0, 20), validators.number.integer],
                3,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'widget'
        },
        {
            id: 'widget-border-shadow',
            param: new BooleanField(glob, 'Widget Frame Shadow', [], function (theme): boolean {
                return theme['base-has-shadow'];
            }),
            section: 'widget',
            deps: ['base-has-shadow']
        },
        {
            id: themeVars.widgetHeaderBackground,
            param: new ColorBoxField(
                glob,
                'Widget Header Background Color',
                [],
                function (theme) {
                    return Color(theme['primary-bg']).lighten(10).hexString(); //mix(Color(theme['body-bg']), 0.8).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widget',
            deps: ['primary-bg']
        },
        {
            id: 'widget-header-bottom-border',
            param: new NumberField(
                glob,
                'Width of bottom border (0-8)',
                [validators.number.range(0, 8), validators.number.integer],
                0
            ),
            section: 'widget'
        },

        {
            id: themeVars.widgetHeaderForeColor,
            param: new ColorBoxField(
                glob,
                'Widget Header Text Color',
                [],
                function (theme) {
                    return Color(theme['on-primary']).hexString();
                },
                { hideTransparency: true }
            ),

            section: 'widget',
            deps: ['on-primary']
        },

        {
            id: 'widget-header-font',
            param: new FontField(
                glob,
                'Font Face',
                [],
                function (theme) {
                    return theme['#font-main'];
                },
                {}
            ),
            section: 'widget',
            group: 'widget-header-font-group',
            deps: ['#font-main']
        },
        {
            id: 'widget-header-font-size',
            param: new SelectField(
                glob,
                'Font Size',
                [],
                function (theme) {
                    return theme['font-main-size'];
                },
                {
                    values: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ],
                    display: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ]
                }
            ),
            section: 'widget',
            group: 'widget-header-font-group',
            deps: ['font-main-size']
        },
        {
            id: 'widget-header-font-weight',
            param: new SelectField(
                glob,
                'Font Weight',
                [],
                function (theme) {
                    return theme['font-main-weight'];
                },
                {
                    values: ['lighter', 'normal', 'bold', 'bolder'],
                    display: ['lighter', 'normal', 'bold', 'bolder']
                }
            ),
            section: 'widget',
            group: 'widget-header-font-group',
            deps: ['font-main-weight']
        },
        {
            id: 'widget-header-font-italic',
            param: new BooleanField(glob, 'Italic', [], function (theme) {
                return theme['font-main-italic'];
            }),
            section: 'widget',
            group: 'widget-header-font-group',
            deps: ['font-main-italic']
        },

        {
            id: 'widget-header-align',
            param: new SelectField(glob, 'Widget Header Alignment', [], 'left', {
                values: ['left', 'center', 'right'],
                display: ['left', 'center', 'right']
            }),
            section: 'widget'
        },

        {
            id: themeVars.columnAutoWidth,
            param: new BooleanField(glob, 'Set Column Width Automatically', [], false),
            section: 'general-widget-grid'
        },

        //LL: 03.05.23  Color for non-empty Search Panel
        {
            id: themeVars.gridSearchPanelColor,
            param: new ColorBoxField(glob, 'Color for non-empty Search Panel', [], '#fef3c5', {
                hideTransparency: false
            }),
            section: 'general-widget-grid'
        },

        {
            id: themeVars.widgetLoadingPanelEnable,
            param: new BooleanField(glob, 'Enable Loading Panel', [], false),
            section: 'widget-capabilities',
            group: 'widget-loading-panel-group'
        },
        {
            id: 'widget-loading-panel-cancel-query',
            param: new BooleanField(glob, 'Cancel Query', [], true),
            section: 'widget-capabilities',
            group: 'widget-loading-panel-group'
        },
        {
            id: 'widget-loading-panel-size',
            param: new SelectField(glob, 'Loading Panel Size', [], 'medium', {
                values: ['small', 'medium', 'large'],
                display: ['small', 'medium', 'large']
            }),
            section: 'widget-capabilities',
            group: 'widget-loading-panel-group'
        },

        {
            id: 'general-widget-grid-header-font',
            param: new FontField(
                glob,
                'Font Face',
                [],
                function (theme) {
                    return theme['#font-main'];
                },
                {}
            ),
            section: 'general-widget-grid',
            group: 'general-widget-grid-header-font-group',
            deps: ['#font-main']
        },
        {
            id: 'general-widget-grid-header-font-size',
            param: new SelectField(
                glob,
                'Font Size',
                [],
                function (theme) {
                    return theme['font-main-size'];
                },
                {
                    values: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ],
                    display: [
                        'default',
                        'xx-small',
                        'x-small',
                        'small',
                        'medium',
                        'large',
                        'x-large',
                        'xx-large',
                        'smaller',
                        'larger'
                    ]
                }
            ),
            section: 'general-widget-grid',
            group: 'general-widget-grid-header-font-group',
            deps: ['font-main-size']
        },
        {
            id: 'general-widget-grid-header-font-weight',
            param: new SelectField(
                glob,
                'Font Weight',
                [],
                function (theme) {
                    return theme['font-main-weight'];
                },
                {
                    values: ['lighter', 'normal', 'bold', 'bolder'],
                    display: ['lighter', 'normal', 'bold', 'bolder']
                }
            ),
            section: 'general-widget-grid',
            group: 'general-widget-grid-header-font-group',
            deps: ['font-main-weight']
        },
        {
            id: 'general-widget-grid-header-font-italic',
            param: new BooleanField(glob, 'Italic', [], function (theme) {
                return theme['font-main-italic'];
            }),
            section: 'general-widget-grid',
            group: 'general-widget-grid-header-font-group',
            deps: ['font-main-italic']
        },
        {
            id: 'widget-bg',
            param: new ColorBoxField(
                glob,
                'Widget Background Color',
                [],
                function (theme) {
                    return Color(theme['surface-bg']).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widget',
            deps: ['surface-bg']
        },
        {
            id: 'widget-hide-overflow',
            param: new BooleanField(
                glob,
                'Clip Widget Content to Container',
                [],
                true //26.06.19: was false, also must update in pdc-platform/src/widgetTheme.template.css
            ),
            section: 'widget'
        },
        {
            id: themeVars.widget_AlwaysShowHeader,
            param: new BooleanField(glob, 'Show Widget Header with Title', [], false),
            section: 'widget'
        },
        {
            id: 'widget-always-title-visible',
            param: new BooleanField(glob, 'Show Title inside widget (for Widgets that supports this)', [], true, {
                allowNull: false
            }),
            section: 'widget'
        },

        {
            id: 'widget-padding',
            param: new NumberField(
                glob,
                'Widget Padding (0-20)',
                [validators.number.range(0, 20), validators.number.integer],
                0,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'widget'
        },

        //LL: 19.09.18: for "Widget Palette"  ===================

        //LL: see in bootstrapTheme.ts:
        //  'gray-base' is 'Main Foreground' in section 'base'
        //  'body-bg' is 'Main Background' in section 'base'

        //  - Third Color:
        {
            id: 'widget-palette-third-color',
            param: new ColorBoxField(glob, 'Third Base Color for Widget', [], null, { hideTransparency: true }),
            section: 'widgetPalette' //,
            //deps: ['gray-base', 'body-bg']
        },

        //  - Fourth Color:
        {
            id: 'widget-palette-fourth-color',
            param: new ColorBoxField(glob, 'Fourth Base Color for Widget', [], null, { hideTransparency: true }),
            section: 'widgetPalette' //,
            //deps: ['gray-base', 'body-bg']
        },

        //  - Third color mix with Fore:
        {
            id: 'widget-palette-mix-fore-third-color-1',
            param: new ColorBoxField(
                glob,
                'Mix Fore with Third Color 1',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-third-color'] != null
                            ? theme['widget-palette-third-color']
                            : theme['body-bg'];
                    return Color(theme['gray-base']).mix(Color(color3), 0.8).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        {
            id: 'widget-palette-mix-fore-third-color-2',
            param: new ColorBoxField(
                glob,
                'Mix Fore with Third Color 2',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-third-color'] != null
                            ? theme['widget-palette-third-color']
                            : theme['body-bg'];
                    return Color(theme['gray-base']).mix(Color(color3), 0.5).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        {
            id: 'widget-palette-mix-fore-third-color-3',
            param: new ColorBoxField(
                glob,
                'Mix Fore with Third Color 3',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-third-color'] != null
                            ? theme['widget-palette-third-color']
                            : theme['body-bg'];
                    return Color(theme['gray-base']).mix(Color(color3), 0.2).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        //  - Third color mix with Back:
        {
            id: 'widget-palette-mix-back-third-color-1',
            param: new ColorBoxField(
                glob,
                'Mix Back with Third Color 1',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-third-color'] != null
                            ? theme['widget-palette-third-color']
                            : theme['gray-base'];
                    return Color(theme['body-bg']).mix(Color(color3), 0.8).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        {
            id: 'widget-palette-mix-back-third-color-2',
            param: new ColorBoxField(
                glob,
                'Mix Back with Third Color 2',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-third-color'] != null
                            ? theme['widget-palette-third-color']
                            : theme['gray-base'];
                    return Color(theme['body-bg']).mix(Color(color3), 0.5).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        {
            id: 'widget-palette-mix-back-third-color-3',
            param: new ColorBoxField(
                glob,
                'Mix Back with Third Color 3',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-third-color'] != null
                            ? theme['widget-palette-third-color']
                            : theme['gray-base'];
                    return Color(theme['body-bg']).mix(Color(color3), 0.2).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        //  - Fourth color mix with Fore:
        {
            id: 'widget-palette-mix-fore-fourth-color-1',
            param: new ColorBoxField(
                glob,
                'Mix Fore with Fourth Color 1',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-fourth-color'] != null
                            ? theme['widget-palette-fourth-color']
                            : theme['body-bg'];
                    return Color(theme['gray-base']).mix(Color(color3), 0.8).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-fourth-color']
        },

        {
            id: 'widget-palette-mix-fore-fourth-color-2',
            param: new ColorBoxField(
                glob,
                'Mix Fore with Fourth Color 2',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-fourth-color'] != null
                            ? theme['widget-palette-fourth-color']
                            : theme['body-bg'];
                    return Color(theme['gray-base']).mix(Color(color3), 0.5).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-fourth-color']
        },

        {
            id: 'widget-palette-mix-fore-fourth-color-3',
            param: new ColorBoxField(
                glob,
                'Mix Fore with Fourth Color 3',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-fourth-color'] != null
                            ? theme['widget-palette-fourth-color']
                            : theme['body-bg'];
                    return Color(theme['gray-base']).mix(Color(color3), 0.2).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-fourth-color']
        },

        //  - Fourth color mix with Back:
        {
            id: 'widget-palette-mix-back-fourth-color-1',
            param: new ColorBoxField(
                glob,
                'Mix Back with Fourth Color 1',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-fourth-color'] != null
                            ? theme['widget-palette-fourth-color']
                            : theme['gray-base'];
                    return Color(theme['body-bg']).mix(Color(color3), 0.8).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-fourth-color']
        },

        {
            id: 'widget-palette-mix-back-fourth-color-2',
            param: new ColorBoxField(
                glob,
                'Mix Back with Fourth Color 2',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-fourth-color'] != null
                            ? theme['widget-palette-fourth-color']
                            : theme['gray-base'];
                    return Color(theme['body-bg']).mix(Color(color3), 0.5).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-fourth-color']
        },

        {
            id: 'widget-palette-mix-back-fourth-color-3',
            param: new ColorBoxField(
                glob,
                'Mix Back with Fourth Color 3',
                [],
                function (theme) {
                    let color3 =
                        theme['widget-palette-fourth-color'] != null
                            ? theme['widget-palette-fourth-color']
                            : theme['gray-base'];
                    return Color(theme['body-bg']).mix(Color(color3), 0.2).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-fourth-color']
        },

        {
            id: 'widget-palette-large-element-back-color',
            param: new ColorBoxField(
                glob,
                'Background Color for Large Element',
                [],
                function (theme) {
                    return Color(theme['gray-base']).mix(Color(theme['body-bg']), 0.8).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg']
        },

        {
            id: 'widget-palette-large-element-fore-color',
            param: new ColorBoxField(
                glob,
                'Fore Color for Large Element',
                [],
                function (theme) {
                    return Color(theme['gray-base']).hexString();
                },
                { hideTransparency: true }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg']
        },

        {
            id: 'widget-palette-large-element-border-color',
            param: new ColorBoxField(
                glob,
                'Border Color for Large Element',
                [],
                function (theme) {
                    return Color(theme['gray-base']).mix(Color(theme['body-bg']), 0.5).hexString();
                },
                { hideTransparency: true }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg']
        },

        {
            id: 'widget-palette-medium-element-back-color',
            param: new ColorBoxField(
                glob,
                'Background Color for Medium Element',
                [],
                //"#dae0f7",
                function (theme) {
                    return Color(theme['widget-palette-mix-back-third-color-3']).hexString();
                },
                { hideTransparency: false }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        {
            id: 'widget-palette-medium-element-fore-color',
            param: new ColorBoxField(
                glob,
                'Fore Color for Medium Element',
                [],
                function (theme) {
                    return Color(theme['widget-palette-mix-fore-third-color-3']).hexString();
                },
                { hideTransparency: true }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        {
            id: 'widget-palette-medium-element-border-color',
            param: new ColorBoxField(
                glob,
                'Border Color for Medium Element',
                [],
                //"#061861",
                function (theme) {
                    return Color(theme['widget-palette-mix-fore-third-color-3']).hexString();
                },
                { hideTransparency: true }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg', 'widget-palette-third-color']
        },

        // {
        //     id: 'widget-palette-small-element-back-color',
        //     param: new ColorBoxField(
        //         glob,
        //         'Background Color for Small Element',
        //         [],
        //         function (theme) {
        //             return Color(theme['gray-base']).mix(Color(theme['body-bg']), 0.9).hexString();
        //         },
        //         { hideTransparency: false }
        //     ),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        // {
        //     id: 'widget-palette-small-element-fore-color',
        //     param: new ColorBoxField(
        //         glob,
        //         'Fore Color for Small Element',
        //         [],
        //         function (theme) {
        //             return Color(theme['gray-base']).hexString();
        //         },
        //         { hideTransparency: true }
        //     ),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        // {
        //     id: 'widget-palette-small-element-border-color',
        //     param: new ColorBoxField(
        //         glob,
        //         'Border Color for Small Element',
        //         [],
        //         function (theme) {
        //             return Color(theme['gray-base']).mix(Color(theme['body-bg']), 0.5).hexString();
        //         },
        //         { hideTransparency: true }
        //     ),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        // {
        //     id: 'widget-palette-tooltips-back-color',
        //     param: new ColorBoxField(
        //         glob,
        //         'Background Color for Tooltips',
        //         [],
        //         function (theme) {
        //             return Color(theme['body-bg']).hexString();
        //         },
        //         { hideTransparency: false }
        //     ),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        // {
        //     id: 'widget-palette-tooltips-fore-color',
        //     param: new ColorBoxField(
        //         glob,
        //         'Fore Color for Tooltips',
        //         [],
        //         function (theme) {
        //             return Color(theme['gray-base']).hexString();
        //         },
        //         { hideTransparency: true }
        //     ),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        // {
        //     id: 'widget-palette-tooltips-border-color',
        //     param: new ColorBoxField(
        //         glob,
        //         'Border Color for Tooltips',
        //         [],
        //         function (theme) {
        //             return Color(theme['gray-base']).mix(Color(theme['body-bg']), 0.5).hexString();
        //         },
        //         { hideTransparency: true }
        //     ),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        // {
        //     id: 'widget-palette-element-color_1',
        //     param: new ColorBoxField(
        //         glob,
        //         'Color 1',
        //         [],
        //         function (theme) {
        //             return Color(theme['gray-base']).hexString();
        //         },
        //         { hideTransparency: true }
        //     ),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        {
            id: 'widget-palette-element-color_2',
            param: new ColorBoxField(
                glob,
                'Color 2',
                [],
                function (theme) {
                    return Color(theme['gray-base']).mix(Color(theme['body-bg']), 0.5).hexString();
                },
                { hideTransparency: true }
            ),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg']
        },

        // {
        //     id: 'widget-palette-element-color_3',
        //     param: new ColorBoxField(glob, 'Color 3', [], '#061861', {
        //         hideTransparency: true
        //     }),
        //     section: 'widgetPalette',
        //     deps: ['gray-base', 'body-bg']
        // },

        {
            id: 'widget-palette-element-color_4',
            param: new ColorBoxField(glob, 'Color 4', [], '#ababab', {
                hideTransparency: true
            }),
            section: 'widgetPalette',
            deps: ['gray-base', 'body-bg']
        },

        // {
        //     id: 'widget-palette-element-special-color_1',
        //     param: new ColorBoxField(glob, 'Special Color 1', [], 'red', {
        //         hideTransparency: true
        //     }),
        //     section: 'widgetPalette' //,
        //     //deps: ['gray-base', 'body-bg']
        // },

        {
            id: 'widget-palette-element-special-color_2',
            param: new ColorBoxField(
                glob,
                'Special Color 2',
                [],
                '#ff0080', // raspberry
                { hideTransparency: true }
            ),
            section: 'widgetPalette' //,
            //deps: ['gray-base', 'body-bg']
        },

        // {
        //     id: 'widget-palette-element-special-color_3',
        //     param: new ColorBoxField(
        //         glob,
        //         'Special Color 3',
        //         [],
        //         '#c7ab50', // mustard
        //         { hideTransparency: true }
        //     ),
        //     section: 'widgetPalette' //,
        //     //deps: ['gray-base', 'body-bg']
        // },

        // black and white
        {
            id: 'widget-palette-black',
            param: new ColorBoxField(glob, 'Black Color', [], '#000000', {
                hideTransparency: true
            }),
            section: 'widgetPalette' //,
            //deps: ['gray-base', 'body-bg']
        },

        {
            id: 'widget-palette-white',
            param: new ColorBoxField(glob, 'White Color', [], '#ffffff', {
                hideTransparency: true
            }),
            section: 'widgetPalette' //,
            //deps: ['gray-base', 'body-bg']
        },

        //LL: FIN for "Widget Palette"  ===================

        {
            id: 'widget-max-cache-age',
            param: new NumberField(
                glob,
                'Max Cache Age in seconds',
                [validators.number.not_negative, validators.number.integer],
                86400,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'caching'
        },

        {
            id: 'widget-min-cache-time',
            param: new NumberField(
                glob,
                'Min Cache time',
                [validators.number.not_negative, validators.number.integer],
                3,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'caching'
        },

        {
            id: themeVars.widgetSQLTimeoutPeriod,
            param: new NumberField(
                glob,
                'SQL Timeout period in seconds',
                [validators.number.not_negative, validators.number.integer],
                60,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'caching'
        },

        {
            // WorkItem [86456]: Limit on Max. number of records, Frontend implementation
            id: themeVars.widgetMaxNumberOfRecords,
            param: new NumberField(
                glob,
                'Max Number of Records in one Data Query',
                [validators.number.not_negative, validators.number.integer],
                1000,
                {
                    min: 0,
                    applyOnEnter: true
                }
            ),
            section: 'caching'
        },

        {
            id: 'widget-queries-nolock',
            param: new BooleanField(
                glob,
                'Use Nolock for all queries to avoid blocking update operations. It is needed for only some Data Sources. Using of it may cause some small inconsistency in result during updates.',
                [],
                false
            ),
            section: 'caching'
        },
        {
            id: themeVars.hideMenuButtonViewMode,
            param: new BooleanField(glob, 'Hide menu button in view mode', [], false),
            section: themeVars.section_widget
        },
        {
            id: 'widget-show-progress-on-load',
            param: new BooleanField(
                glob,
                'Show progress bar on Widget load. Use it when multiple widgets of the same type are to be loaded',
                [],
                false
            ),
            section: themeVars.section_widget
        },

        {
            id: themeVars.enableClickToFilter,
            param: new BooleanField(glob, 'Enable', [], false),
            group: themeVars.group_clickToFilter,
            section: themeVars.section_widget
        },
        {
            id: themeVars.clickToFilterMode,
            param: new SelectField(glob, 'Mode', [], 'instantApply', {
                values: ['instantApply', 'delayedApply', 'cumulative', 'drillThrough'],
                display: [
                    'Apply immediately after click',
                    'Apply after a delay',
                    'Allow multiple selections',
                    'Drill Through'
                ]
            }),
            group: themeVars.group_clickToFilter,
            section: themeVars.section_widget
        },
        {
            id: themeVars.drillThroughWorkspaceID,
            param: new NumberField(
                glob,
                'Drill Through Workspace ID',
                [validators.number.not_negative, validators.number.integer],
                null,
                {
                    min: 0,
                    applyOnEnter: true,
                    allowNull: true
                }
            ),
            group: themeVars.group_clickToFilter,
            section: themeVars.section_widget
        },
        {
            id: themeVars.drillThroughMode,
            param: new SelectField(glob, 'Drill Through Mode', [], 'openInNewTab', {
                values: ['openInNewTab', 'openInExistingTab'],
                display: ['Always open a new tab', 'Open in an existing tab']
            }),
            group: themeVars.group_clickToFilter,
            section: themeVars.section_widget
        },
        {
            id: themeVars.drillThroughTabTitleTemplate,
            param: new StringField(
                glob,
                'Drill Through Tab Title Template (supports either {{ }} or <% %> delimiters not both)',
                [],
                null,
                { allowNull: true }
            ),
            group: themeVars.group_clickToFilter,
            section: themeVars.section_widget
        },

        {
            id: themeVars.nullHandling_behaviour,
            param: new RadioGroupField(
                glob,
                'Null Handling Behaviour',
                [],
                nullHandlingBehaviour.ERROR_IF_NULLS, //null,
                {
                    layout: 'vertical',
                    name: 'RG_nullHandling_behaviour', //LL: needed to differ RadioGroup from other RadioGroups
                    //title: "behaviour",  //  -- TRY
                    //layout: "vertical",  //  default "horizontal"  -- TRY
                    //width: "300px",  // default undef      -- TRY
                    items: [
                        {
                            text: 'Error if Nulls (Default)',
                            value: nullHandlingBehaviour.ERROR_IF_NULLS
                        },
                        {
                            text: 'Default handling in each Widget',
                            value: nullHandlingBehaviour.WIDGET_DEFAULT_HANDLING
                        },
                        {
                            text: 'Omit records with Nulls',
                            value: nullHandlingBehaviour.OMIT_NULLS
                        },
                        {
                            text: 'User defined replace of Nulls',
                            value: nullHandlingBehaviour.REPLACE_NULLS
                        }
                    ]
                    //displayField: "text",  //LL: is default, no need
                    //valueField: "value"  //LL: is default, no need
                }
            ),
            section: 'nullHandling'
        },

        {
            id: themeVars.nullHandling_replaceForString,
            param: new StringField(
                glob,
                'for Strings:',
                [
                    //validators.string.notEmpty,
                    validators.string.notAllBlanks //LL: includes notEmpty
                ],
                REPLACE_NULL_STRING_DEFAULT //"<no value>"   // "&ltno value&gt"
            ),
            section: 'nullHandling'
        },

        //LL: 23.04.18  ReplaceNullValuesField
        {
            id: themeVars.nullHandling_replaceForNumeric,
            param: new ReplaceNullValuesField(glob, 'for Numeric:', [], null, {
                type: 'numeric'
                //type: ReplaceNullValuesField.EditComponent.valueTypes.numeric
            }),
            section: 'nullHandling'
        },

        //LL: 24.04.18  ReplaceNullValuesField
        {
            id: themeVars.nullHandling_replaceForBoolean,
            param: new ReplaceNullValuesField(glob, 'for Boolean:', [], null, {
                type: 'boolean'
                //type: ReplaceNullValuesField.EditComponent.valueTypes.boolean
            }),
            section: 'nullHandling'
        },

        //LL: 24.04.18  ReplaceNullValuesField
        {
            id: themeVars.nullHandling_replaceForDate,
            param: new ReplaceNullValuesField(glob, 'for Date:', [], null, {
                type: 'date'
                //type: ReplaceNullValuesField.EditComponent.valueTypes.date
            }),
            section: 'nullHandling'
        }
    ]);

    appTheme.registerPreRender(function (input) {
        var vars = {};

        return _.extend(input, vars);
    });

    appTheme.registerTemplate(template, ['widget']);

    _init.push(glob);
}
